import Service from '@ember/service';
import { restartableTask } from 'ember-concurrency';

import type SocialProfileModel from 'later/models/social-profile';
import type {
  AudioSearchParam,
  AudioSearchResult,
  InstagramAudioTab,
  InstagramAudioType
} from 'shared/types/instagram-audio';

/**
 * Wraps the Instagram Audio API (`/ig_audio`), which returns audio assets
 * (music and original sounds) that can be attached to Instagram Reels at
 * creation time.
 *
 * The Graph `/ig_audio` endpoint requires an `appsecret_proof` (an HMAC keyed
 * with the Facebook App Secret), so it cannot be called from the browser.
 * Requests are proxied through the backend
 * (`GET /api/v2/social_profiles/:id/audio_search`), which signs them.
 *
 * This API is only available for Instagram profiles connected through the
 * Facebook (Graph) API — it is not supported for Instagram Login profiles.
 *
 * @see https://developers.facebook.com/docs/instagram-platform/content-publishing/audio-api/
 */
export default class InstagramAudioService extends Service {
  /**
   * Searches the Instagram Audio API (via the backend proxy) for audio assets.
   *
   * The proxy always requires an `audio_type` (`music` or `original_sound`).
   * When no `query` is supplied the API returns trending audio for that type.
   * The `trending` tab therefore maps to `music` with no query.
   *
   * Pass `after` (the cursor from a previous {@link AudioSearchResult}) to
   * fetch the next page of results.
   */
  searchAudio = restartableTask(
    async (
      socialProfile: SocialProfileModel,
      tab: InstagramAudioTab,
      query = '',
      after: string | undefined = undefined
    ): Promise<AudioSearchResult> => {
      const params: AudioSearchParam = {
        audio_type: this.#audioTypeForTab(tab)
      };

      const trimmedQuery = query.trim();
      if (trimmedQuery) {
        params.query = trimmedQuery;
      }

      if (after) {
        params.after = after;
      }

      const response = await socialProfile.audioSearch(params);

      return {
        assets: response?.audio ?? [],
        nextCursor: response?.paging?.cursors?.after ?? undefined
      };
    }
  );

  #audioTypeForTab(tab: InstagramAudioTab): InstagramAudioType {
    return tab === 'original_audio' ? 'original_sound' : 'music';
  }
}

declare module '@ember/service' {
  interface Registry {
    'social/instagram-audio': InstagramAudioService;
  }
}
